// Workspace-scoped Supabase query helper. The intranet has dozens of
// `sb().from('<table>')...` call sites. In Phase 3 multi-tenancy, every
// query against the 28 workspace-scoped tables must be filtered by
// `workspace_id = <active>` so RLS (enabled in the migration follow-up)
// doesn't strip rows.
//
// Instead of updating each call site by hand (fragile, easy to miss),
// this module exposes a thin wrapper that:
//
//   1. Returns a Supabase query builder.
//   2. When USE_MULTITENANT is on AND the target table is in the
//      `WORKSPACE_SCOPED` set, the active workspace_id is automatically
//      .eq()'d onto the chain.
//   3. Provides insertScoped() / updateScoped() helpers that also set
//      workspace_id on writes.
//
// Flag off today — zero behavior change. Adopt call-site-by-call-site
// by replacing `sb().from('X')` with `scoped().from('X')`. Old call
// sites continue to work through RLS once migrations are applied and
// the flag flips on.

import { getSupabase } from '../../shared/functions/supabaseClient.js'
import { USE_MULTITENANT } from './featureFlags.js'

// Re-export for back-compat with any call-site still reading from here.
export { USE_MULTITENANT }

// The 28 governance tables that get `workspace_id` in Phase 3. Keep in
// sync with migrations 0046–0050. Tables NOT in this set (e.g. profiles,
// user_preferences, calendar_events, notifications — user-scoped or
// intrinsically cross-org) are passed through unchanged.
export const WORKSPACE_SCOPED = new Set([
  // chat
  'chat_channels', 'chat_channel_members', 'chat_messages', 'chat_reactions', 'chat_mentions',
  // tickets
  'tickets', 'ticket_comments', 'ticket_columns', 'ticket_field_values', 'stories',
  // docs / kb
  'documents', 'kb_articles', 'notes', 'links', 'resource_links',
  // meet
  'meet_rooms', 'meet_room_members', 'meet_participants', 'meet_transcripts',
  'meet_transcript_analyses', 'meet_waiting_room', 'meetings',
  // announcements + kudos + activity + project fields
  'announcements', 'announcement_reactions', 'kudos',
  'standup_activity', 'activity_events',
  'project_fields'
])

// Read the active workspace from the JWT-mirrored claims on root state.
// Callers must pass the root — DOMQL handlers always have `s.root`.
export const activeWorkspaceId = (root) =>
  root?.jwtClaims?.active_workspace_id || null

// Convenience predicate for UI gating when rendering depends on a
// concrete workspace id being present.
export const hasActiveWorkspace = (root) => !!activeWorkspaceId(root)

// Returns a wrapped from() that auto-filters by workspace_id when
// applicable. Usage:
//
//   scopedFrom(root, 'chat_messages').select('*').order('created_at')
//
// Non-scoped tables (e.g. 'profiles') pass through unchanged.
export const scopedFrom = (root, table) => {
  const sb = getSupabase()
  const q = sb.from(table)
  if (!USE_MULTITENANT) return q
  if (!WORKSPACE_SCOPED.has(table)) return q
  const wsId = activeWorkspaceId(root)
  if (!wsId) return q   // no active workspace yet → let RLS decide (will return empty)
  return q.eq('workspace_id', wsId)
}

// Insert with workspace_id auto-set. `row` is merged with
// `workspace_id: <active>` for scoped tables.
export const insertScoped = (root, table, row) => {
  const sb = getSupabase()
  if (USE_MULTITENANT && WORKSPACE_SCOPED.has(table)) {
    const wsId = activeWorkspaceId(root)
    if (wsId) row = { ...row, workspace_id: wsId }
  }
  return sb.from(table).insert(row)
}

// Update helper — caller still supplies .eq(...) as usual; this just
// forwards to the unscoped from() because updates shouldn't rewrite
// workspace_id (rows live in one workspace for their lifetime).
export const updateScoped = (root, table) => {
  const sb = getSupabase()
  return sb.from(table).update // returns function — caller .update({...}).eq(...)
}

// Realtime channel helper — returns a Supabase realtime channel with
// a filter that includes workspace_id. Caller provides the table,
// event type, and any additional filters.
//
//   const ch = realtimeScoped(root, 'chat_messages', 'INSERT',
//     `channel_id=eq.${channelId}`)
//     .on(...).subscribe()
//
// Off today — returns the standard channel unchanged. Turns on with
// USE_MULTITENANT so old call sites don't need conditional code.
export const realtimeScoped = (root, table, event = '*', extraFilter = null) => {
  const sb = getSupabase()
  const channelName = `rt-${table}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
  const ch = sb.channel(channelName)
  const filters = []
  if (USE_MULTITENANT && WORKSPACE_SCOPED.has(table)) {
    const wsId = activeWorkspaceId(root)
    if (wsId) filters.push(`workspace_id=eq.${wsId}`)
  }
  if (extraFilter) filters.push(extraFilter)
  const filter = filters.join(',') || undefined
  return { channel: ch, event, table, filter, channelName }
}
